import { ChangeDetectionStrategy, Component, ViewChild, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { DynamicFormComponent } from './dynamic-form.component';
import type { FormDefinition } from './form.types';

/**
 * MTY-themed MatDialog wrapper. White editorial header with a chip
 * eyebrow, a large Lexend title and a circular close button; sticky
 * footer with a landing-style Save pill (`rounded-[16px]` primary fill,
 * `hover:opacity-90 active:scale-95`). Body is the DynamicFormComponent.
 *
 * The Save button lives outside the form and submits it via the
 * `form="<id>"` attribute — keeps the footer sticky while preserving
 * native submit semantics.
 */
@Component({
  selector: 'app-form-modal',
  standalone: true,
  imports: [MatDialogModule, MatIconModule, TranslateModule, DynamicFormComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <header class="flex items-start justify-between gap-4 px-6 md:px-8 pt-7 pb-6 bg-white border-b border-black/5">
      <div class="flex flex-col gap-3 min-w-0">
        @if (data.eyebrowKey) {
          <span class="self-start inline-flex px-3 py-1 rounded-full bg-mty-primary/10 text-mty-primary font-alternate text-[11px] font-semibold tracking-wider uppercase">
            {{ data.eyebrowKey | translate }}
          </span>
        }
        <h2 class="font-alternate text-[26px] md:text-[28px] leading-tight font-bold text-black m-0">
          {{ data.titleKey | translate }}
        </h2>
      </div>
      <button
        type="button"
        (click)="ref.close()"
        class="shrink-0 w-10 h-10 rounded-full inline-flex items-center justify-center text-black/60 hover:bg-black/5 hover:text-black transition-colors"
        [attr.aria-label]="'Cancel' | translate">
        <mat-icon>close</mat-icon>
      </button>
    </header>

    <app-dynamic-form
      #dyn
      [fields]="data.fields"
      [model]="data.model"
      (submitted)="ref.close($event)">
    </app-dynamic-form>

    <footer class="bg-white px-6 md:px-8 py-4 flex items-center justify-between gap-4 shadow-[0_-1px_2px_rgba(0,0,0,0.04)]">
      <div class="font-base text-[13px] text-black/60 flex items-center gap-2 min-h-6">
        <ng-content select="[formStatus]"></ng-content>
      </div>
      <div class="flex items-center gap-2">
        <button
          type="button"
          (click)="ref.close()"
          data-cy="modal-cancel"
          class="px-4 py-2.5 rounded-[16px] font-alternate text-[14px] font-medium text-black/70 hover:bg-black/5 transition-colors">
          {{ 'Cancel' | translate }}
        </button>
        <button
          type="submit"
          [attr.form]="dyn.formId"
          data-cy="modal-save"
          class="px-5 py-2.5 rounded-[16px] bg-mty-primary text-white font-alternate text-[14px] font-semibold shadow-sm hover:opacity-90 active:scale-95 transition-all">
          {{ 'Save' | translate }}
        </button>
      </div>
    </footer>
  `,
})
export class FormModalComponent<TModel> {
  @ViewChild('dyn') readonly dyn?: DynamicFormComponent<TModel>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- erased at injection boundary
  readonly data = inject<FormDefinition<any>>(MAT_DIALOG_DATA) as FormDefinition<TModel>;
  readonly ref = inject<MatDialogRef<FormModalComponent<TModel>, TModel>>(MatDialogRef);
}
